// Chat mock data
export interface Conversation {
  id: string;
  type: 'direct' | 'group';
  name?: string; // For group chats
  participants: string[];
  participantNames: string[];
  lastMessage: string;
  lastMessageAt: Date;
  unreadCount: number;
  avatar?: string;
  isOnline?: boolean;
  isMuted?: boolean;
  isPinned?: boolean;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  type: 'text' | 'image' | 'file' | 'system';
  attachments?: Attachment[];
  reactions?: Reaction[];
  replyTo?: string;
  createdAt: Date;
  readBy: string[];
  isEdited?: boolean;
}

export interface Attachment {
  id: string;
  name: string;
  type: 'image' | 'file';
  url: string;
  size: number;
}

export interface Reaction {
  emoji: string;
  userIds: string[];
}

export interface ChatContact {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  lastSeen?: Date;
  department: string;
}

export interface ChatGroup {
  id: string;
  name: string;
  description: string;
  avatar?: string;
  memberIds: string[];
  memberNames: string[];
  createdBy: string;
  createdAt: Date;
  isPrivate: boolean;
}

export const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    type: 'direct',
    participants: ['u1', 'u2'],
    participantNames: ['John Doe', 'Jane Smith'],
    lastMessage: 'Sure, I will review the PR this afternoon',
    lastMessageAt: new Date('2024-01-21T10:30:00'),
    unreadCount: 2,
    avatar: '/avatars/2.jpg',
    isOnline: true,
    isPinned: true,
  },
  {
    id: 'conv-2',
    type: 'group',
    name: 'Engineering Team',
    participants: ['u1', 'u2', 'u4', 'u5', 'u7', 'u11'],
    participantNames: ['John Doe', 'Jane Smith', 'Bob Williams', 'Charlie Brown', 'Ethan Hunt', 'Ian Foster'],
    lastMessage: 'Ian: Deployment completed successfully!',
    lastMessageAt: new Date('2024-01-21T09:45:00'),
    unreadCount: 5,
    isPinned: true,
  },
  {
    id: 'conv-3',
    type: 'direct',
    participants: ['u1', 'u3'],
    participantNames: ['John Doe', 'Alice Johnson'],
    lastMessage: 'The new designs look great!',
    lastMessageAt: new Date('2024-01-21T09:15:00'),
    unreadCount: 0,
    avatar: '/avatars/3.jpg',
    isOnline: true,
  },
  {
    id: 'conv-4',
    type: 'group',
    name: 'Product Launch',
    participants: ['u1', 'u6', 'u12', 'u20'],
    participantNames: ['John Doe', 'Diana Prince', 'Julia Roberts', 'Rachel Green'],
    lastMessage: 'Diana: Let\'s sync tomorrow at 2pm',
    lastMessageAt: new Date('2024-01-20T17:30:00'),
    unreadCount: 0,
    isMuted: true,
  },
  {
    id: 'conv-5',
    type: 'direct',
    participants: ['u1', 'u5'],
    participantNames: ['John Doe', 'Charlie Brown'],
    lastMessage: 'Can you help me with the API integration?',
    lastMessageAt: new Date('2024-01-20T16:00:00'),
    unreadCount: 1,
    avatar: '/avatars/5.jpg',
    isOnline: false,
  },
  {
    id: 'conv-6',
    type: 'direct',
    participants: ['u1', 'u8'],
    participantNames: ['John Doe', 'Fiona Green'],
    lastMessage: 'Thanks for the feedback!',
    lastMessageAt: new Date('2024-01-20T14:20:00'),
    unreadCount: 0,
    avatar: '/avatars/8.jpg',
    isOnline: true,
  },
  {
    id: 'conv-7',
    type: 'group',
    name: 'Design Review',
    participants: ['u1', 'u3', 'u8', 'u10', 'u18'],
    participantNames: ['John Doe', 'Alice Johnson', 'Fiona Green', 'Hannah Davis', 'Pam Beesly'],
    lastMessage: 'Pam: Updated the mockups',
    lastMessageAt: new Date('2024-01-19T11:00:00'),
    unreadCount: 0,
  },
  {
    id: 'conv-8',
    type: 'direct',
    participants: ['u1', 'u15'],
    participantNames: ['John Doe', 'Michael Scott'],
    lastMessage: 'That\'s what she said! 😂',
    lastMessageAt: new Date('2024-01-18T15:45:00'),
    unreadCount: 0,
    avatar: '/avatars/15.jpg',
    isOnline: false,
  },
];

export const mockMessages: Message[] = [
  // Conversation 1 - John & Jane
  {
    id: 'msg-1-1',
    conversationId: 'conv-1',
    senderId: 'u2',
    senderName: 'Jane Smith',
    content: 'Hey John, can you review the PR for the new feature?',
    type: 'text',
    createdAt: new Date('2024-01-21T09:00:00'),
    readBy: ['u1', 'u2'],
  },
  {
    id: 'msg-1-2',
    conversationId: 'conv-1',
    senderId: 'u1',
    senderName: 'John Doe',
    content: 'Sure, which PR is it?',
    type: 'text',
    createdAt: new Date('2024-01-21T09:05:00'),
    readBy: ['u1', 'u2'],
  },
  {
    id: 'msg-1-3',
    conversationId: 'conv-1',
    senderId: 'u2',
    senderName: 'Jane Smith',
    content: 'PR #234 - Dashboard redesign',
    type: 'text',
    createdAt: new Date('2024-01-21T09:10:00'),
    readBy: ['u1', 'u2'],
  },
  {
    id: 'msg-1-4',
    conversationId: 'conv-1',
    senderId: 'u2',
    senderName: 'Jane Smith',
    content: 'Here are the screenshots',
    type: 'image',
    attachments: [
      { id: 'att-1', name: 'dashboard-v2.png', type: 'image', url: '/images/dashboard-v2.png', size: 245678 },
    ],
    createdAt: new Date('2024-01-21T09:12:00'),
    readBy: ['u1', 'u2'],
  },
  {
    id: 'msg-1-5',
    conversationId: 'conv-1',
    senderId: 'u1',
    senderName: 'John Doe',
    content: 'Looks good! I will take a closer look',
    type: 'text',
    reactions: [{ emoji: '👍', userIds: ['u2'] }],
    createdAt: new Date('2024-01-21T10:00:00'),
    readBy: ['u1', 'u2'],
  },
  {
    id: 'msg-1-6',
    conversationId: 'conv-1',
    senderId: 'u2',
    senderName: 'Jane Smith',
    content: 'Sure, I will review the PR this afternoon',
    type: 'text',
    createdAt: new Date('2024-01-21T10:30:00'),
    readBy: ['u2'],
  },

  // Conversation 2 - Engineering Team
  {
    id: 'msg-2-1',
    conversationId: 'conv-2',
    senderId: 'u11',
    senderName: 'Ian Foster',
    content: 'Starting the deployment now',
    type: 'text',
    createdAt: new Date('2024-01-21T09:30:00'),
    readBy: ['u2', 'u4', 'u5', 'u7', 'u11'],
  },
  {
    id: 'msg-2-2',
    conversationId: 'conv-2',
    senderId: 'u7',
    senderName: 'Ethan Hunt',
    content: 'All tests passed, good to go!',
    type: 'text',
    createdAt: new Date('2024-01-21T09:35:00'),
    readBy: ['u2', 'u4', 'u5', 'u7', 'u11'],
  },
  {
    id: 'msg-2-3',
    conversationId: 'conv-2',
    senderId: 'u11',
    senderName: 'Ian Foster',
    content: 'Deployment completed successfully!',
    type: 'text',
    reactions: [
      { emoji: '🎉', userIds: ['u2', 'u4', 'u5', 'u7'] },
      { emoji: '🚀', userIds: ['u2', 'u5'] },
    ],
    createdAt: new Date('2024-01-21T09:45:00'),
    readBy: ['u2', 'u4', 'u5', 'u7', 'u11'],
  },
];

export const mockChatContacts: ChatContact[] = [
  { id: 'u2', name: 'Jane Smith', email: 'jane.smith@company.com', avatar: '/avatars/2.jpg', status: 'online', department: 'Engineering' },
  { id: 'u3', name: 'Alice Johnson', email: 'alice.johnson@company.com', avatar: '/avatars/3.jpg', status: 'online', department: 'Design' },
  { id: 'u4', name: 'Bob Williams', email: 'bob.williams@company.com', avatar: '/avatars/4.jpg', status: 'busy', department: 'Engineering' },
  { id: 'u5', name: 'Charlie Brown', email: 'charlie.brown@company.com', avatar: '/avatars/5.jpg', status: 'offline', lastSeen: new Date('2024-01-21T08:00:00'), department: 'Engineering' },
  { id: 'u6', name: 'Diana Prince', email: 'diana.prince@company.com', avatar: '/avatars/6.jpg', status: 'online', department: 'Product' },
  { id: 'u7', name: 'Ethan Hunt', email: 'ethan.hunt@company.com', avatar: '/avatars/7.jpg', status: 'away', department: 'Engineering' },
  { id: 'u8', name: 'Fiona Green', email: 'fiona.green@company.com', avatar: '/avatars/8.jpg', status: 'online', department: 'Design' },
  { id: 'u10', name: 'Hannah Davis', email: 'hannah.davis@company.com', avatar: '/avatars/10.jpg', status: 'online', department: 'Design' },
  { id: 'u11', name: 'Ian Foster', email: 'ian.foster@company.com', avatar: '/avatars/11.jpg', status: 'busy', department: 'Engineering' },
  { id: 'u15', name: 'Michael Scott', email: 'michael.scott@company.com', avatar: '/avatars/15.jpg', status: 'offline', lastSeen: new Date('2024-01-20T18:00:00'), department: 'Sales' },
];

export const mockChatGroups: ChatGroup[] = [
  {
    id: 'group-1',
    name: 'Engineering Team',
    description: 'Main channel for engineering discussions',
    memberIds: ['u1', 'u2', 'u4', 'u5', 'u7', 'u11'],
    memberNames: ['John Doe', 'Jane Smith', 'Bob Williams', 'Charlie Brown', 'Ethan Hunt', 'Ian Foster'],
    createdBy: 'u2',
    createdAt: new Date('2023-01-15'),
    isPrivate: false,
  },
  {
    id: 'group-2',
    name: 'Product Launch',
    description: 'Coordination for upcoming product launches',
    memberIds: ['u1', 'u6', 'u12', 'u20'],
    memberNames: ['John Doe', 'Diana Prince', 'Julia Roberts', 'Rachel Green'],
    createdBy: 'u6',
    createdAt: new Date('2024-01-05'),
    isPrivate: true,
  },
  {
    id: 'group-3',
    name: 'Design Review',
    description: 'Weekly design review and feedback',
    memberIds: ['u1', 'u3', 'u8', 'u10', 'u18'],
    memberNames: ['John Doe', 'Alice Johnson', 'Fiona Green', 'Hannah Davis', 'Pam Beesly'],
    createdBy: 'u10',
    createdAt: new Date('2023-06-20'),
    isPrivate: false,
  },
  {
    id: 'group-4',
    name: 'All Company',
    description: 'Company-wide announcements and updates',
    memberIds: ['u1', 'u2', 'u3', 'u4', 'u5', 'u6', 'u7', 'u8', 'u10', 'u11', 'u12', 'u14', 'u15'],
    memberNames: ['All employees'],
    createdBy: 'u14',
    createdAt: new Date('2022-01-01'),
    isPrivate: false,
  },
];

// Chat stats
export const chatStats = {
  totalConversations: 8,
  unreadMessages: 8,
  onlineContacts: 6,
  totalContacts: 10,
  totalGroups: 4,
};
